import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ISavedQuote, SavedQuotesService } from '@techspert-io/transcriptions';
import { EMPTY, Observable, combineLatest, map, tap } from 'rxjs';

export interface ICallOption {
  groupId: string;
  transcriptFileId: string;
  callNumber: number;
}

export interface IViewTranscriptEvent {
  groupId: string;
  startTime: number;
}

@Component({
  selector: 'app-expert-quotes',
  template: `@if (savedQuotes$ | async; as savedQuotes) {
    <div class="flex flex-col gap-4">
      <div>
        <table mat-table class="saved-quotes-table" [dataSource]="savedQuotes">
          <ng-container matColumnDef="call">
            <th mat-header-cell *matHeaderCellDef [style.width]="'90px'">
              Call
            </th>
            <td mat-cell *matCellDef="let element" style="padding: 8px 12px">
              @if (getCall(element); as call) {
                Call {{ call.callNumber }}
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="quote">
            <th mat-header-cell *matHeaderCellDef>Quote</th>
            <td mat-cell *matCellDef="let element" style="padding: 8px 12px">
              {{ element.quote }}
            </td>
          </ng-container>
          <ng-container matColumnDef="startTime">
            <th mat-header-cell *matHeaderCellDef [style.width]="'150px'">
              Timecode
            </th>
            <td mat-cell *matCellDef="let element" style="padding: 8px 12px">
              @if (isTranscriptViewable(element)) {
                <button
                  type="button"
                  style="cursor: pointer; text-decoration: underline; color: blue; background: none; border: none; padding: 0; font: inherit;"
                  (click)="onViewTranscriptClick(element)"
                >
                  {{ element.startTime | quoteTimestamp }}
                </button>
              } @else {
                <span>
                  {{ element.startTime | quoteTimestamp }}
                </span>
              }
            </td>
          </ng-container>
          <tr *matNoDataRow>
            <td>No expert quotes currently saved.</td>
          </tr>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
        <mat-paginator
          class="saved-quotes-paginator"
          [pageSize]="5"
          [pageSizeOptions]="[5, 10, 25, 100]"
        >
        </mat-paginator>
      </div>
    </div>
  }`,
  standalone: false,
})
export class ExpertQuotesComponent implements OnInit {
  @ViewChild(MatPaginator) matPaginator: MatPaginator;
  @Input() expertId: string;
  @Input() calls: ICallOption[] = [];

  @Output() viewTranscript = new EventEmitter<IViewTranscriptEvent>();

  displayedColumns: string[] = ['quote', 'startTime'];

  savedQuotes$: Observable<MatTableDataSource<ISavedQuote>> = EMPTY;

  constructor(private savedQuotesService: SavedQuotesService) {}

  ngOnInit() {
    this.displayedColumns =
      this.calls.length > 1
        ? ['call', 'quote', 'startTime']
        : ['quote', 'startTime'];

    this.savedQuotes$ = combineLatest([
      this.savedQuotesService.allQuotes$,
      this.savedQuotesService.getQuotesByExpertId(this.expertId),
    ]).pipe(
      map(([data]) =>
        (data[this.expertId] || []).sort((a, b) => a.startTime - b.startTime)
      ),
      map((data) => new MatTableDataSource(data)),
      tap((dataSource) => {
        dataSource.paginator = this.matPaginator;
      })
    );
  }

  getCall(element: ISavedQuote): ICallOption | undefined {
    return this.calls.find((call) => call.groupId === element.transcriptFileId);
  }

  isTranscriptViewable(element: ISavedQuote): boolean {
    return !!this.getCall(element);
  }

  onViewTranscriptClick(element: ISavedQuote): void {
    const call = this.getCall(element);

    if (!call) {
      return;
    }

    this.viewTranscript.emit({
      groupId: call.groupId,
      startTime: element.startTime,
    });
  }
}
